import { normalizeId } from './ids';
import { isGroupToken } from './itemGroups';
import { sortThenOf, sortThenPatch } from './sortLevels';
import { savedFilterViewPatch } from './savedFilterView';

/**
 * Where a saved filter opens (KPR-1124): the collection's List with the
 * filter applied, written into the address exactly as the list's own picker
 * applies one (CollectionItemsPage `applySavedFilter`) — its text in `q`, its
 * sort in `sort` and `dir` and its further levels in `sort2` … `dir3`
 * (KPR-1636), its grouping period in `group`, a board
 * (KPR-1395) in `view=board`, `board` and `lanes`, and what it opens in
 * (KPR-1641): its card in `card`, its view mode in `view` and its table
 * columns in `cols` (utils/savedFilterView), and which filter it is in
 * `saved` (KPR-1642: the list's saved filter menu then names it).
 *
 * Every part the filter carries is written, defaults included: the list drops
 * a value equal to its default, but a person's stored per-collection sort and
 * grouping would then outvote the filter on the next visit. A part the filter
 * does not carry (the API says `null`) is left out, so it leaves the reader's
 * own choice alone, as the picker does. The filter is read when Home is read,
 * so an edit to it changes where the pin goes with no re-pin.
 */
export function savedFilterPath(collectionId, filter) {
	const cid = normalizeId(collectionId);
	if (!cid) return null;
	const params = new URLSearchParams();
	const query = typeof filter?.query === 'string' ? filter.query.trim() : '';
	if (query) params.set('q', query);
	if (filter?.sort_field) params.set('sort', filter.sort_field);
	if (filter?.sort_direction) params.set('dir', filter.sort_direction);
	sortThenOf(filter?.sort_then).forEach((level, i) => {
		params.set(`sort${i + 2}`, level.field);
		params.set(`dir${i + 2}`, level.direction);
	});
	if (isGroupToken(filter?.group_by)) params.set('group', filter.group_by);
	// What it opens in (KPR-1641): its card, view mode and columns, each only
	// when it carries one. A board without a view still opens as the board.
	const view = savedFilterViewPatch(filter);
	if (view.card) params.set('card', view.card);
	if (view.view) params.set('view', view.view);
	// Its board (KPR-1395): the columns and lanes the board draws by.
	if (filter?.board?.by) {
		params.set('board', filter.board.by);
		if (filter.board.lanes) params.set('lanes', filter.board.lanes);
	}
	if (view.cols) params.set('cols', view.cols);
	// Which filter it is (KPR-1642), so the list's saved filter menu names it.
	const id = normalizeId(filter?._id);
	if (id) params.set('saved', id);
	const search = params.toString();
	return `/collections/${cid}/items${search ? `?${search}` : ''}`;
}

/**
 * The address patch (hooks/useItemsViewParams `update`) that applies a saved
 * filter's ORDER: `sort` / `dir` and the further levels (KPR-1636). A filter
 * that carries an order — a level 1 or any further level — writes all of it,
 * its levels replacing the address's (none clears them), so the list never
 * shows the filter's level 1 under someone else's level 2. A filter with no
 * order returns `{}` and leaves the reader's alone, as before. A field the
 * filter does not set (`sort_field: null` under levels) leaves level 1 alone.
 */
export function savedFilterSortPatch(filter) {
	const levels = sortThenOf(filter?.sort_then);
	if (!filter?.sort_field && !levels.length) return {};
	return {
		...(filter.sort_field ? { sort: filter.sort_field } : {}),
		...(filter.sort_direction ? { dir: filter.sort_direction } : {}),
		...sortThenPatch(levels),
	};
}


/**
 * The ONE address patch that applies a saved filter on the items page
 * (`applySavedFilter`): its query, what it opens in (card, view, columns;
 * KPR-1641), its board (KPR-1395, which opens the list as the board), its
 * sort (with its "then by" levels, KPR-1637) and its grouping period, each
 * only where it stored one, so the list
 * never renders in between and a filter without a part leaves it alone. The
 * page keeps the side effects (the stored board and period).
 */
export function savedFilterApplyPatch(filter) {
	const text = filter?.query;
	const patch = { ...savedFilterViewPatch(filter), q: text ? `${text}`.trim() : undefined };
	if (filter?.board?.by) {
		patch.view = 'board';
		patch.board = filter.board.by;
		patch.lanes = filter.board.lanes || undefined;
	}
	// Its order, the further levels included (KPR-1637): all of it replaces the
	// address's, none of it leaves the reader's alone.
	Object.assign(patch, savedFilterSortPatch(filter));
	if (isGroupToken(filter?.group_by)) patch.group = filter.group_by;
	return patch;
}
